import {
  Typography,
  Box,
  TextField,
  CircularProgress,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, SubmitHandler } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useLoginMutation } from "features/auth/auth";
import {
  extractLoginErrorMessage,
  LOGIN_ERROR_FALLBACK,
  type ReadableLoginField,
} from "features/auth/extractLoginErrorMessage";
import { useAuth } from "features/store";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { LOGO_URL } from "config";
import { NOTISTACK_TOP_RIGHT_FAILURE_OPTIONS } from "constants/index";

type LoginInputs = {
  username: string;
  password: string;
};

// A field this form submits that extractLoginErrorMessage does not read would
// degrade that field's own serializer error to the generic fallback, with
// nothing failing to say so. Adding one here is a typecheck error until it is
// added there too.
type AssertReadable<T extends ReadableLoginField> = T;
type LoginFieldsAreReadable = AssertReadable<keyof LoginInputs>;

export default function Login() {
  const [login, { isLoading }] = useLoginMutation();
  const { isAuthenticated } = useAuth();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInputs>();

  // `unwrap()` is what surfaces the rejection: the mutation itself resolves
  // either way, so without it a failed sign-in left the user on an unchanged
  // form with the reason discarded.
  const onSubmit: SubmitHandler<LoginInputs> = async (data) => {
    try {
      await login(data).unwrap();
    } catch (error) {
      enqueueSnackbar(extractLoginErrorMessage(error, LOGIN_ERROR_FALLBACK), {
        ...NOTISTACK_TOP_RIGHT_FAILURE_OPTIONS,
        // The banner auto-hides on the provider's shared timer; this is so a
        // reader who has finished with it need not wait the timer out.
        action: (key) => (
          <IconButton
            size="small"
            aria-label="Dismiss"
            sx={{ color: "inherit" }}
            onClick={() => closeSnackbar(key)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      // A banner enqueued here outlives this page: notistack sits above the
      // router, so a fail-then-succeed within the auto-hide window carried a
      // stale failure into the authenticated app — and into the one corner the
      // Header occupies, which is the whole reason this anchors top-right.
      closeSnackbar();
      navigate("/");
    }
  }, [isAuthenticated, navigate, closeSnackbar]);

  return (
    <Box
      bgcolor={"white"}
      padding={5}
      borderRadius={3}
      boxShadow={2}
    >
      <Box flexDirection="row" display="flex" justifyContent={"space-between"} alignItems={"center"} marginBottom={5} >
                  <img width={300} src={LOGO_URL} />
        <Typography variant="h5">
          Log In
        </Typography>
      </Box>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        marginTop={1}
      >
        <TextField
          margin="normal"
          required
          fullWidth
          id="username"
          label="Username"
          autoComplete="username"
          autoFocus
          {...register("username")}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          {...register("password")}
        />
        {isLoading ? (
          <CircularProgress sx={{ mt: 2, mb: 2 }} />
        ) : (
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Log In
          </Button>
        )}
      </Box>
    </Box>
  );
}
